import { useEffect, useMemo, useState } from 'react'
import GShell from './GShell.jsx'
import MemberPicker, { useMembersMap, memberName } from './MemberPicker.jsx'
import { Title, Card, Pill, Modal, Field, Err, money, fmtDate } from './gui.jsx'
import { LESSON_STATUSES, watchLessons, createLesson, setLessonStatus, addLessonNote, addLessonPayment, deleteLesson } from '../../lib/lessons.js'

export default function GLessons() {
  const [raw, setRaw] = useState({})
  const [detail, setDetail] = useState(null)
  const [f, setF] = useState({ memberId: '', packageName: '', instructor: '', scheduledDate: '' })
  const [err, setErr] = useState('')
  const members = useMembersMap()
  useEffect(() => { const off = watchLessons(setRaw); return () => off() }, [])
  const rows = useMemo(() => Object.values(raw || {}).filter(Boolean).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)), [raw])
  const add = async () => { setErr(''); try { await createLesson(f); setF({ memberId: '', packageName: '', instructor: '', scheduledDate: '' }) } catch (e) { setErr(e?.message || 'Failed') } }
  const live = detail ? rows.find((r) => r.id === detail.id) : null
  return (
    <GShell>
      <Title title="Lessons" subtitle="Instruction packages, notes, and (record-only) payments." />
      <Card>
        <div className="mb-2"><MemberPicker value={f.memberId} onChange={(v) => setF((s) => ({ ...s, memberId: v }))} optional={false} /></div>
        <div className="grid sm:grid-cols-3 gap-2">
          <input className="input" placeholder="Package (e.g. 5-lesson)" value={f.packageName} onChange={(e) => setF((s) => ({ ...s, packageName: e.target.value }))} />
          <input className="input" placeholder="Instructor" value={f.instructor} onChange={(e) => setF((s) => ({ ...s, instructor: e.target.value }))} />
          <input className="input" type="date" value={f.scheduledDate} onChange={(e) => setF((s) => ({ ...s, scheduledDate: e.target.value }))} />
        </div>
        <button className="btn-primary text-sm mt-2" onClick={add} disabled={!f.memberId}>Book Lesson</button>
        <Err msg={err} />
      </Card>
      <section className="pb-4 grid md:grid-cols-2 gap-3">
        {rows.length === 0 ? <p className="text-sm text-gray-500">No lessons.</p> : rows.map((l) => {
          const paid = Object.values(l.payments || {}).reduce((s, p) => s + (Number(p.amount) || 0), 0)
          return (
            <Card key={l.id}>
              <div className="flex items-start justify-between gap-2">
                <div><div className="font-extrabold" style={{ color: 'var(--ge-primary)' }}>{memberName(members, l.memberId)}</div>
                  <div className="text-xs text-gray-500">{l.packageName || 'Lesson'}{l.instructor ? ` · ${l.instructor}` : ''}{l.scheduledDate ? ` · ${fmtDate(l.scheduledDate)}` : ''}</div></div>
                <Pill>{l.status}</Pill>
              </div>
              <div className="text-xs text-gray-500 mt-1">{Object.keys(l.notesHistory || {}).length} notes · {money(paid)} recorded</div>
              <div className="mt-2 flex gap-2 pt-2 border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                <button className="btn-secondary text-xs" onClick={() => setDetail({ id: l.id })}>Open</button>
                <button className="btn-ghost text-xs ml-auto" style={{ color: '#B91C1C' }} onClick={async () => { if (window.confirm('Delete lesson?')) await deleteLesson(l.id) }}>Delete</button>
              </div>
            </Card>
          )
        })}
      </section>
      {live ? <LessonModal l={live} members={members} onClose={() => setDetail(null)} /> : null}
    </GShell>
  )
}

function LessonModal({ l, members, onClose }) {
  const [note, setNote] = useState('')
  const [pay, setPay] = useState('')
  const notes = Object.values(l.notesHistory || {}).sort((a, b) => (b.ts || 0) - (a.ts || 0))
  const pays = Object.values(l.payments || {}).sort((a, b) => (b.ts || 0) - (a.ts || 0))
  return (
    <Modal title={`${memberName(members, l.memberId)} — ${l.packageName || 'Lesson'}`} onClose={onClose} wide>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs text-gray-500">Status:</span>
        <select className="input text-sm" value={l.status} onChange={(e) => setLessonStatus(l.id, e.target.value)}>{LESSON_STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Notes</div>
          <div className="flex gap-1"><input className="input flex-1" placeholder="Add a note" value={note} onChange={(e) => setNote(e.target.value)} /><button className="btn-secondary text-xs" onClick={async () => { if (note.trim()) { await addLessonNote(l.id, note); setNote('') } }}>Add</button></div>
          <div className="mt-2 text-xs space-y-1 max-h-40 overflow-y-auto">{notes.map((n, i) => <div key={i} className="border-b py-0.5" style={{ borderColor: 'rgba(0,0,0,0.05)' }}>{n.text} <span className="text-gray-400">· {new Date(n.ts).toLocaleDateString()}</span></div>)}</div>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Payments (record-only)</div>
          <div className="flex gap-1"><input className="input flex-1" type="number" step="0.01" placeholder="$ amount" value={pay} onChange={(e) => setPay(e.target.value)} /><button className="btn-secondary text-xs" onClick={async () => { if (Number(pay) > 0) { await addLessonPayment(l.id, { amount: pay, note: 'lesson payment' }); setPay('') } }}>Record</button></div>
          <p className="text-[10px] text-gray-400 mt-1">Record-only — no card is charged.</p>
          <div className="mt-2 text-xs space-y-1 max-h-40 overflow-y-auto">{pays.map((p, i) => <div key={i} className="border-b py-0.5 flex justify-between" style={{ borderColor: 'rgba(0,0,0,0.05)' }}><span>{money(p.amount)}</span><span className="text-gray-400">{new Date(p.ts).toLocaleDateString()}</span></div>)}</div>
        </div>
      </div>
    </Modal>
  )
}
